import type { TableRunStatus } from "@Simplicity-Education/core";
import type { CategoryColor, SortDirection } from "@simplicity/ui";
import {
  AdminTable,
  AdminTableBody,
  AdminTableHeader,
  Button,
  CategoryBadge,
  FormSection,
  SortableColumnHeader,
} from "@simplicity/ui";
import { ChevronDown, ChevronRight, Copy, Plus } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import { TableRunStatusContext, type TableRunStatusContextValue } from "../TableRunStatusContext";
import { AddTablesDialog } from "./AddTablesDialog";
import { ConnectorTableRow } from "./ConnectorTableRow";
import { LastGeneratedCell } from "./LastGeneratedCell";
import { type ParkedNames, removeTableAt, type TablesChange, updateTableAt } from "./tableWrites";
import type { Schedules, TableConfig } from "./types";

type SortColumn = "name" | "schedule" | "lastGenerated";

function getTableCadences(schedules: Schedules, tableName: string): string[] {
  return Object.entries(schedules)
    .filter(([, entries]) => entries.some((e) => e.name === tableName))
    .map(([cadence]) => cadence);
}

const CADENCE_COLORS: Record<string, CategoryColor> = {
  daily: "emerald",
  hourly: "amber",
  weekly: "violet",
};

function ScheduleBadges({ cadences }: { cadences: string[] }) {
  if (cadences.length === 0) {
    return <span className="text-xs text-muted-foreground">&mdash;</span>;
  }
  return (
    <div className="flex gap-1">
      {cadences.map((c) => (
        <CategoryBadge key={c} color={CADENCE_COLORS[c] ?? "slate"}>
          {c.charAt(0).toUpperCase() + c.slice(1)}
        </CategoryBadge>
      ))}
    </div>
  );
}

/**
 * Sort key for the Last written column. Never-generated tables sort as -1 so
 * that ascending puts them first — "what was never set up correctly" is the
 * question this column exists to answer, followed by the stalest.
 */
function lastGeneratedSortKey(run: TableRunStatus | null): number {
  if (run == null) return -1;
  return run.lastWrittenAt == null ? -1 : Date.parse(run.lastWrittenAt);
}

function getSortedIndices(
  tables: TableConfig[],
  schedules: Schedules,
  sortColumn: SortColumn,
  sortDirection: SortDirection,
  runFor: (tableName: string) => TableRunStatus | null,
): number[] {
  return tables
    .map((_, i) => i)
    .sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      if (sortColumn === "name") {
        return (
          dir *
          (tables[a].name || "").localeCompare(tables[b].name || "", undefined, {
            sensitivity: "base",
          })
        );
      }
      if (sortColumn === "lastGenerated") {
        return (
          dir *
          (lastGeneratedSortKey(runFor(tables[a].name)) -
            lastGeneratedSortKey(runFor(tables[b].name)))
        );
      }
      const countA = getTableCadences(schedules, tables[a].name).length;
      const countB = getTableCadences(schedules, tables[b].name).length;
      return dir * (countA - countB);
    });
}

/**
 * Resolve one table's freshness from the context, or null when it cannot be resolved. Only "ready"
 * resolves: "unavailable" MUST fall through to null so the column reads an em-dash rather than
 * "Never", because an empty anchor map is indistinguishable from tables that genuinely never ran,
 * and reporting a failed fetch as "never generated" is SIM-2729's defect.
 */
function resolveFor(
  ctx: TableRunStatusContextValue | null,
  tableName: string,
): TableRunStatus | null {
  // Only "ready" resolves. "unavailable" MUST fall through to null so the column
  // reads em-dash rather than "Never": an absent row is indistinguishable from a
  // table that genuinely has never been written, and reporting a failed fetch as
  // "never" is the lying-UI defect SIM-2729 exists to remove.
  if (ctx?.status !== "ready") return null;
  return ctx.byTable.get(tableName.trim().toLowerCase()) ?? null;
}

function TablesTableHeader({
  sortColumn,
  sortDirection,
  onToggle,
}: {
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onToggle: (c: SortColumn) => void;
}) {
  return (
    <AdminTableHeader>
      <tr>
        <th>
          <SortableColumnHeader
            active={sortColumn === "name"}
            direction={sortDirection}
            onClick={() => onToggle("name")}
          >
            Name
          </SortableColumnHeader>
        </th>
        <th className="w-[180px]">
          <SortableColumnHeader
            active={sortColumn === "schedule"}
            direction={sortDirection}
            onClick={() => onToggle("schedule")}
          >
            Schedule
          </SortableColumnHeader>
        </th>
        <th className="w-[150px]">
          <SortableColumnHeader
            active={sortColumn === "lastGenerated"}
            direction={sortDirection}
            onClick={() => onToggle("lastGenerated")}
          >
            Last written
          </SortableColumnHeader>
        </th>
      </tr>
    </AdminTableHeader>
  );
}

// Schedules a scroll-to-bottom on the next animation frame and cancels the
// pending frame on unmount. Prevents "scrollRef.current?.scrollTo is not a
// function" if the component is torn down before the RAF fires (SIM-1033).
function useScrollToBottomOnUnmountSafe() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  function scheduleScrollToBottom() {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const el = scrollRef.current;
      // Guard against post-unmount invocation and non-DOM stand-ins (jsdom).
      if (el && typeof el.scrollTo === "function") {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }
    });
  }

  return { scrollRef, scheduleScrollToBottom };
}

function useTableSort() {
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  function toggleSort(column: SortColumn) {
    if (sortColumn === column) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  }

  return { sortColumn, sortDirection, toggleSort };
}

function TablesEditorActions({
  canCopy,
  onAddBlank,
  onOpenCopy,
}: {
  canCopy: boolean;
  onAddBlank: () => void;
  onOpenCopy: () => void;
}) {
  return (
    <div className="flex gap-2">
      {canCopy && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1 text-xs"
          onClick={onOpenCopy}
        >
          <Copy className="h-3 w-3" aria-hidden="true" /> Add from…
        </Button>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1 text-xs"
        onClick={onAddBlank}
      >
        <Plus className="h-3 w-3" aria-hidden="true" /> Add table
      </Button>
    </div>
  );
}

interface TablesEditorProps {
  tables: TableConfig[];
  schedules: Schedules;
  /** One call per edit: a change that moves schedule entries carries both halves, so the form writes once. */
  onChange: (change: TablesChange) => void;
  connectorType: string;
  /**
   * SIM-3182: enables "Add from…". Absent on any surface that edits tables
   * before a connector row exists, where there is no peer set to copy from.
   */
  connectorId?: string;
}

export function TablesEditor({
  tables,
  schedules,
  onChange,
  connectorType,
  connectorId,
}: TablesEditorProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [addFromOpen, setAddFromOpen] = useState(false);
  const { sortColumn, sortDirection, toggleSort } = useTableSort();
  const { scrollRef, scheduleScrollToBottom } = useScrollToBottomOnUnmountSafe();
  const parkedRef = useRef<ParkedNames>(new Map());

  function apply(plan: { change: TablesChange; parked: ParkedNames }) {
    parkedRef.current = plan.parked;
    onChange(plan.change);
  }

  function updateTable(index: number, table: TableConfig) {
    apply(updateTableAt(tables, schedules, parkedRef.current, index, table));
  }

  function removeTable(index: number) {
    // SIM-3044: a schedule entry whose table no longer exists reaches the
    // runner as a bare `{ name }` with no fields or primaryKey, and fails on
    // page 0 on every run forever with nothing to attribute it to (RV lost two
    // Skyward tables this way for 6 days).
    apply(removeTableAt(tables, schedules, parkedRef.current, index));
    if (expandedIndex === index) setExpandedIndex(null);
    else if (expandedIndex !== null && expandedIndex > index) setExpandedIndex(expandedIndex - 1);
  }

  function addTable() {
    onChange({ tables: [...tables, { name: "", fields: [], primaryKey: "" }] });
    setExpandedIndex(tables.length);
    scheduleScrollToBottom();
  }

  function addCopiedTables(copied: TableConfig[]) {
    if (copied.length === 0) return;
    onChange({ tables: [...tables, ...copied] });
    scheduleScrollToBottom();
  }

  // Null when no provider is mounted (create-connector form, and every existing
  // spec) — the column then renders em-dashes rather than breaking.
  const runCtx = useContext(TableRunStatusContext);
  const runFor = (tableName: string) => resolveFor(runCtx, tableName);

  const sortedIndices = getSortedIndices(tables, schedules, sortColumn, sortDirection, runFor);

  return (
    <FormSection
      title="Tables"
      count={tables.length}
      indent={false}
      actions={
        <TablesEditorActions
          canCopy={connectorId != null}
          onAddBlank={addTable}
          onOpenCopy={() => setAddFromOpen(true)}
        />
      }
    >
      {tables.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No tables configured. Click &quot;Add table&quot; to get started.
        </div>
      ) : (
        <AdminTable ref={scrollRef} maxHeight="500px" aria-label="Tables">
          <TablesTableHeader
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onToggle={toggleSort}
          />
          <AdminTableBody className="[&_tr:last-child]:border-0">
            {sortedIndices.map((i) => (
              <TableRowWithExpand
                key={i}
                table={tables[i]}
                index={i}
                cadences={getTableCadences(schedules, tables[i].name)}
                run={runFor(tables[i].name)}
                runLoaded={runCtx?.status !== "loading"}
                nowMs={runCtx?.computedAtMs ?? 0}
                lookbackDays={runCtx?.lookbackDays ?? 0}
                isExpanded={expandedIndex === i}
                onToggleExpand={() => setExpandedIndex(expandedIndex === i ? null : i)}
                onChange={(t) => updateTable(i, t)}
                onRemove={() => removeTable(i)}
                connectorType={connectorType}
              />
            ))}
          </AdminTableBody>
        </AdminTable>
      )}
      {connectorId && (
        <AddTablesDialog
          open={addFromOpen}
          onOpenChange={setAddFromOpen}
          connectorId={connectorId}
          existingTables={tables}
          onAdd={addCopiedTables}
        />
      )}
    </FormSection>
  );
}

function TableRowWithExpand({
  table,
  index,
  cadences,
  run,
  runLoaded,
  nowMs,
  lookbackDays,
  isExpanded,
  onToggleExpand,
  onChange,
  onRemove,
  connectorType,
}: {
  table: TableConfig;
  index: number;
  cadences: string[];
  run: TableRunStatus | null;
  runLoaded: boolean;
  nowMs: number;
  lookbackDays: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onChange: (table: TableConfig) => void;
  onRemove: () => void;
  connectorType: string;
}) {
  return (
    <>
      {/* The row keeps its whole-row click target, but the accessible control is
          the disclosure button in the name cell — NOT `role="button"` on the
          <tr>. A row with a button role makes every interactive child a
          `nested-interactive` violation, which is what the "Last written"
          popover trigger tripped (SIM-2729). Mouse users keep the big target;
          keyboard and AT users get a real button with aria-expanded. */}
      <tr className="cursor-pointer" onClick={onToggleExpand}>
        <td>
          <button
            type="button"
            aria-expanded={isExpanded}
            className="inline-flex items-center gap-1.5 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={(e) => {
              // The row handler would otherwise fire too and toggle twice.
              e.stopPropagation();
              onToggleExpand();
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
            {table.name || <span className="italic text-muted-foreground">untitled</span>}
          </button>
        </td>
        <td>
          <ScheduleBadges cadences={cadences} />
        </td>
        <td>
          <LastGeneratedCell
            run={run}
            loaded={runLoaded}
            nowMs={nowMs}
            lookbackDays={lookbackDays}
          />
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-foreground/[0.03]">
          <td colSpan={3} className="p-0 font-sans">
            <ConnectorTableRow
              table={table}
              index={index}
              onChange={onChange}
              onRemove={onRemove}
              isExpanded={true}
              onToggleExpand={onToggleExpand}
              connectorType={connectorType}
            />
          </td>
        </tr>
      )}
    </>
  );
}
